import { AnimatePresence, motion } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  Grid3x3,
  HelpCircle,
  Maximize2,
  RotateCcw,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const ZOOM_STEPS = [100, 125, 150, 200];
const MIN_SLIDER_DISTANCE = 2; // % — distance minimale entre deux sliders

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/** Calcule les positions par défaut des sliders (répartition uniforme) */
const defaultPositions = (count) =>
  Array.from({ length: count - 1 }, (_, i) => ((i + 1) / count) * 100);

/** Clamp une valeur entre min et max */
const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

/**
 * Retourne un tableau de positions validées :
 * - chaque position respecte la distance minimale avec ses voisines
 * - l'ordre est préservé
 */
const validatePositions = (positions, changed, newVal) => {
  const next = [...positions];
  next[changed] = newVal;

  // Push vers la droite
  for (let i = changed; i < next.length - 1; i++) {
    if (next[i + 1] < next[i] + MIN_SLIDER_DISTANCE) {
      next[i + 1] = next[i] + MIN_SLIDER_DISTANCE;
    }
  }
  // Push vers la gauche
  for (let i = changed; i > 0; i--) {
    if (next[i - 1] > next[i] - MIN_SLIDER_DISTANCE) {
      next[i - 1] = next[i] - MIN_SLIDER_DISTANCE;
    }
  }
  return next.map((p) => clamp(p, 0, 100));
};

// ─────────────────────────────────────────────────────────────────────────────
// HINT OVERLAY — apparaît une seule fois, disparaît au premier drag
// ─────────────────────────────────────────────────────────────────────────────

const DragHint = ({ onDismiss }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.3 }}
    className="absolute inset-0 z-50 flex items-center justify-center pointer-events-none"
  >
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0 }}
      transition={{ delay: 0.2 }}
      className="flex flex-col items-center gap-3 px-6 py-4 rounded-2xl border border-white/20 bg-black/70 backdrop-blur-xl"
    >
      <div className="flex items-center gap-3">
        <motion.div
          animate={{ x: [-8, 8, -8] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          className="flex items-center gap-1 text-white/70"
        >
          <ChevronLeft size={16} />
          <div className="w-0.5 h-6 bg-white/50 rounded-full" />
          <ChevronRight size={16} />
        </motion.div>
      </div>
      <p className="text-xs text-white/60 font-medium">
        Glissez la barre pour comparer
      </p>
    </motion.div>
  </motion.div>
);

// ─────────────────────────────────────────────────────────────────────────────
// SLIDER HANDLE — poignée draggable
// ─────────────────────────────────────────────────────────────────────────────

const SliderHandle = ({
  position,       // % 0-100
  index,
  label,          // ex: "A / B"
  isDragging,
  onMouseDown,
  onTouchStart,
}) => {
  return (
    <div
      className="absolute inset-y-0 z-40"
      style={{ left: `${position}%`, transform: 'translateX(-50%)' }}
    >
      {/* Ligne verticale */}
      <div
        className={`absolute inset-y-0 left-1/2 -translate-x-1/2 w-0.5 transition-opacity ${
          isDragging ? 'bg-white opacity-100' : 'bg-white/70 opacity-80'
        }`}
      />

      {/* Zone de grab élargie (invisible) + handle visible */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 flex items-center justify-center cursor-ew-resize touch-none select-none"
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        title="Glissez pour déplacer"
      >
        {/* Capsule visible */}
        <motion.div
          animate={{ scale: isDragging ? 1.1 : 1 }}
          transition={{ duration: 0.12 }}
          className={`flex flex-col items-center justify-center gap-1.5 w-7 py-3 rounded-full border transition-all ${
            isDragging
              ? 'bg-white border-white shadow-2xl shadow-white/30'
              : 'bg-black/80 border-white/40 hover:border-white/80 hover:bg-black/90 backdrop-blur-sm'
          }`}
        >
          <ChevronLeft
            size={10}
            className={isDragging ? 'text-black' : 'text-white/70'}
          />
          <div
            className={`w-0.5 h-3 rounded-full ${isDragging ? 'bg-black/30' : 'bg-white/20'}`}
          />
          <ChevronRight
            size={10}
            className={isDragging ? 'text-black' : 'text-white/70'}
          />
        </motion.div>
      </div>

      {/* Indicateur de position — visible pendant le drag */}
      <AnimatePresence>
        {isDragging && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            className="absolute top-3 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-md bg-white text-black text-[10px] font-bold whitespace-nowrap shadow-lg"
          >
            {Math.round(position)}%
          </motion.div>
        )}
      </AnimatePresence>

      {/* Label de séparation (zone gauche / zone droite) */}
      {label && !isDragging && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded bg-black/60 backdrop-blur-sm text-[9px] text-white/50 whitespace-nowrap pointer-events-none">
          {label}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// IMAGE LAYER — une couche de l'image avec son clipping
// ─────────────────────────────────────────────────────────────────────────────

const ImageLayer = ({ src, alt, label, clipLeft, clipRight, zoom, panX, panY }) => {
  const clipPath = `inset(0 ${100 - clipRight}% 0 ${clipLeft}%)`;

  return (
    <div
      className="absolute inset-0 overflow-hidden"
      style={{ clipPath }}
    >
      {/* Image avec zoom géré via transform (pas animate pour éviter le recalcul de clip) */}
      <div
        className="absolute inset-0 transition-transform duration-200"
        style={{
          transform: `scale(${zoom / 100}) translate(${panX}px, ${panY}px)`,
          transformOrigin: 'center center',
        }}
      >
        <img
          src={src}
          alt={alt || label}
          className="w-full h-full object-cover"
          draggable={false}
        />
      </div>

      {/* Label de la couche */}
      <div
        className="absolute bottom-3 pointer-events-none"
        style={{
          left: `${clipLeft}%`,
          right: `${100 - clipRight}%`,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <div className="px-2.5 py-1 rounded-lg bg-black/60 backdrop-blur-sm border border-white/10">
          <span className="text-[10px] font-semibold text-white/70 whitespace-nowrap">
            {label}
          </span>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// HELP PANEL
// ─────────────────────────────────────────────────────────────────────────────

const HelpPanel = ({ onClose }) => (
  <motion.div
    initial={{ opacity: 0, y: -6 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -6 }}
    className="p-4 rounded-xl border border-white/10 bg-white/[0.02] mb-4"
  >
    <div className="flex items-center justify-between mb-3">
      <p className="text-xs font-semibold text-white">Comment utiliser le comparateur</p>
      <button onClick={onClose} className="p-1 rounded hover:bg-white/10 transition-colors">
        <X size={12} className="text-slate-500" />
      </button>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {[
        { label: 'Déplacer un séparateur', desc: 'Cliquez et faites glisser la barre verticale pour révéler plus ou moins d\'une couche.' },
        { label: 'Zoomer', desc: 'Utilisez les boutons + et − pour agrandir. Le zoom s\'applique à toutes les couches simultanément.' },
        { label: 'Grille de référence', desc: 'Activez la grille pour aligner vos observations et repérer des éléments précis.' },
        { label: 'Réinitialiser', desc: 'Remettez les séparateurs au centre et le zoom à 100% en un clic.' },
      ].map((h, i) => (
        <div key={i} className="flex gap-2.5">
          <ChevronRight size={12} className="text-cyan-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-xs font-semibold text-white">{h.label}</p>
            <p className="text-[11px] text-slate-500 leading-relaxed mt-0.5">{h.desc}</p>
          </div>
        </div>
      ))}
    </div>
  </motion.div>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

/**
 * VisualComparator refondu
 *
 * Props :
 *   config {
 *     images: Array<{ url: string, label: string }>
 *     height?: string   — hauteur CSS de la zone de comparaison (défaut : '60vh')
 *     showGrid?: boolean — grille activée par défaut
 *   }
 *   resolveAsset?: (path: string) => string
 */
const VisualComparator = ({ config = {}, resolveAsset }) => {
  const images = useMemo(() => config.images || [], [config.images]);
  const imageCount = images.length;

  const [positions, setPositions] = useState(() => defaultPositions(imageCount));
  const [activeSlider, setActiveSlider] = useState(null); // index ou null
  const [zoom, setZoom] = useState(100);
  const [panX] = useState(0);
  const [panY] = useState(0);
  const [showGrid, setShowGrid] = useState(config.showGrid ?? false);
  const [showHelp, setShowHelp] = useState(false);
  const [showHint, setShowHint] = useState(true); // hint de drag au premier chargement

  const containerRef = useRef(null);
  const isDraggingRef = useRef(false);

  // Masque le hint au premier drag
  const dismissHint = useCallback(() => setShowHint(false), []);

  // ── Gestion du drag souris ────────────────────────────────────────────────

  const getPercentFromMouseX = useCallback((clientX) => {
    if (!containerRef.current) return 50;
    const rect = containerRef.current.getBoundingClientRect();
    return clamp(((clientX - rect.left) / rect.width) * 100, 0, 100);
  }, []);

  const handleMouseDown = useCallback((sliderIndex) => (e) => {
    e.preventDefault();
    setActiveSlider(sliderIndex);
    isDraggingRef.current = true;
    dismissHint();
  }, [dismissHint]);

  useEffect(() => {
    if (activeSlider === null) return;

    const onMove = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const pct = getPercentFromMouseX(clientX);
      setPositions((prev) => validatePositions(prev, activeSlider, pct));
    };

    const onUp = () => {
      setActiveSlider(null);
      isDraggingRef.current = false;
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onUp);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [activeSlider, getPercentFromMouseX]);

  // ── Touch start sur un slider ─────────────────────────────────────────────

  const handleTouchStart = useCallback((sliderIndex) => (e) => {
    e.stopPropagation();
    setActiveSlider(sliderIndex);
    dismissHint();
  }, [dismissHint]);

  // ── Zoom ─────────────────────────────────────────────────────────────────

  const currentZoomIndex = ZOOM_STEPS.indexOf(zoom);

  const zoomIn = () => {
    if (currentZoomIndex < ZOOM_STEPS.length - 1) {
      setZoom(ZOOM_STEPS[currentZoomIndex + 1]);
    }
  };

  const zoomOut = () => {
    if (currentZoomIndex > 0) {
      setZoom(ZOOM_STEPS[currentZoomIndex - 1]);
    }
  };

  // ── Reset ─────────────────────────────────────────────────────────────────

  const handleReset = () => {
    setPositions(defaultPositions(imageCount));
    setZoom(100);
    setShowGrid(config.showGrid ?? false);
  };

  // ── Validation ────────────────────────────────────────────────────────────

  if (imageCount < 2) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-600 text-sm">
        Configuration insuffisante — au moins 2 images requises.
      </div>
    );
  }

  // ── Calcul des zones de clip par image ───────────────────────────────────

  const getClip = (idx) => {
    const left = idx === 0 ? 0 : positions[idx - 1];
    const right = idx === imageCount - 1 ? 100 : positions[idx];
    return { clipLeft: left, clipRight: right };
  };

  // ── Libellé de chaque séparateur ─────────────────────────────────────────

  const getSliderLabel = (idx) => {
    const leftLabel = images[idx]?.label || `Couche ${idx + 1}`;
    const rightLabel = images[idx + 1]?.label || `Couche ${idx + 2}`;
    return `${leftLabel} / ${rightLabel}`;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="w-full space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-white">
            {config.title || 'Comparateur visuel'}
          </h2>
          <p className="text-[10px] text-slate-500 mt-0.5">
            {imageCount} couche{imageCount > 1 ? 's' : ''} · {imageCount - 1} séparateur{imageCount > 2 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowHelp((v) => !v)}
            className={`p-2 rounded-xl border transition-all ${
              showHelp
                ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-400'
                : 'border-white/10 bg-white/[0.03] text-slate-500 hover:text-white'
            }`}
            title="Aide"
          >
            <HelpCircle size={14} />
          </button>
          <button
            onClick={handleReset}
            className="p-2 rounded-xl border border-white/10 bg-white/[0.03] text-slate-500 hover:text-white hover:border-white/20 transition-all"
            title="Réinitialiser"
          >
            <RotateCcw size={14} />
          </button>
        </div>
      </div>

      {/* Help panel */}
      <AnimatePresence>
        {showHelp && <HelpPanel onClose={() => setShowHelp(false)} />}
      </AnimatePresence>

      {/* Image legend */}
      <div className="flex flex-wrap gap-2">
        {images.map((img, i) => (
          <div key={i} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-white/[0.08] bg-white/[0.02]">
            <div
              className="w-1.5 h-1.5 rounded-full"
              style={{
                background: ['#22d3ee', '#f59e0b', '#a78bfa', '#34d399', '#f87171'][i % 5],
              }}
            />
            <span className="text-[10px] text-slate-400 font-medium">{img.label || `Couche ${i + 1}`}</span>
          </div>
        ))}
      </div>

      {/* Main comparison area */}
      <div
        ref={containerRef}
        className="relative rounded-2xl overflow-hidden border border-white/[0.08] bg-[#020b18] select-none"
        style={{
          height: config.height || '60vh',
          minHeight: 280,
          cursor: activeSlider !== null ? 'ew-resize' : 'default',
        }}
        // Empêche le scroll pendant le drag sur mobile
        onTouchMove={(e) => { if (activeSlider !== null) e.preventDefault(); }}
      >
        {/* Images layers */}
        {images.map((img, i) => {
          const { clipLeft, clipRight } = getClip(i);
          const src = resolveAsset ? resolveAsset(img.url) : img.url;
          return (
            <ImageLayer
              key={i}
              src={src}
              alt={img.label}
              label={img.label || `Couche ${i + 1}`}
              clipLeft={clipLeft}
              clipRight={clipRight}
              zoom={zoom}
              panX={panX}
              panY={panY}
            />
          );
        })}

        {/* Grid overlay */}
        {showGrid && (
          <div
            className="absolute inset-0 pointer-events-none opacity-20 mix-blend-overlay"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.15) 1px, transparent 1px)',
              backgroundSize: '48px 48px',
            }}
          />
        )}

        {/* Slider handles */}
        {positions.map((pos, i) => (
          <SliderHandle
            key={i}
            position={pos}
            index={i}
            label={getSliderLabel(i)}
            isDragging={activeSlider === i}
            onMouseDown={handleMouseDown(i)}
            onTouchStart={handleTouchStart(i)}
          />
        ))}

        {/* Drag hint — disparaît au premier drag */}
        <AnimatePresence>
          {showHint && <DragHint onDismiss={dismissHint} />}
        </AnimatePresence>
      </div>

      {/* Controls bar */}
      <div className="flex items-center justify-between px-1">
        {/* Zoom controls */}
        <div className="flex items-center gap-2">
          <button
            onClick={zoomOut}
            disabled={currentZoomIndex === 0}
            className="p-2 rounded-xl border border-white/10 bg-white/[0.03] text-slate-500 hover:text-white hover:border-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            title="Dézoomer"
          >
            <ZoomOut size={14} />
          </button>

          {/* Zoom level pills */}
          <div className="flex items-center gap-1">
            {ZOOM_STEPS.map((step) => (
              <button
                key={step}
                onClick={() => setZoom(step)}
                className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-all ${
                  zoom === step
                    ? 'bg-white/15 text-white border border-white/20'
                    : 'text-slate-600 hover:text-slate-400'
                }`}
              >
                {step}%
              </button>
            ))}
          </div>

          <button
            onClick={zoomIn}
            disabled={currentZoomIndex === ZOOM_STEPS.length - 1}
            className="p-2 rounded-xl border border-white/10 bg-white/[0.03] text-slate-500 hover:text-white hover:border-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            title="Zoomer"
          >
            <ZoomIn size={14} />
          </button>
        </div>

        {/* Right controls */}
        <div className="flex items-center gap-2">
          {/* Grid toggle */}
          <button
            onClick={() => setShowGrid((v) => !v)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-medium transition-all ${
              showGrid
                ? 'border-white/25 bg-white/10 text-white'
                : 'border-white/[0.08] bg-transparent text-slate-600 hover:text-slate-400 hover:border-white/15'
            }`}
            title="Grille de référence"
          >
            <Grid3x3 size={13} />
            <span>Grille</span>
          </button>

          {/* Plein écran (ouvre dans nouvel onglet) */}
          {images.length === 2 && (
            <button
              onClick={() => {
                const src = resolveAsset ? resolveAsset(images[0].url) : images[0].url;
                window.open(src, '_blank');
              }}
              className="p-2 rounded-xl border border-white/[0.08] bg-transparent text-slate-600 hover:text-white hover:border-white/20 transition-all"
              title="Ouvrir l'image originale"
            >
              <Maximize2 size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Position summary — discret, utile pour des comparaisons précises */}
      {positions.length > 0 && (
        <div className="flex items-center gap-3 px-1">
          {positions.map((pos, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <div className="w-0.5 h-3 bg-white/20 rounded-full" />
              <span className="text-[10px] text-slate-700">
                Séparateur {i + 1} — {Math.round(pos)}%
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VisualComparator;